import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import api from "../../lib/api";
import { saveContratarPagoCtx, submitRedsysForm } from "../../lib/redsysForm";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Select } from "../../components/ui/Select";
import { Logo } from "../../components/Logo";
import { PublicPageFrame } from "../../components/layout/PublicPageFrame";
import { AceptacionCondicionesPago } from "../../components/legal/AceptacionCondicionesPago";
import { useLegalComercio } from "../../context/LegalComercioContext";
import type { LegalVendedorInfo } from "../../content/legalComercio";
import { PROVINCIAS_ESPANA_OPTIONS } from "../../constants/provincias";

const cardClass =
  "bg-white/[0.92] backdrop-blur-[8px] rounded-2xl shadow-2xl p-8 w-full border border-white/20 max-w-lg";

const TITULO_SEGURO = "Seguro para Accidentes en circuito";

function fechaMadridActual(): string {
  const parts = new Intl.DateTimeFormat("es-ES", {
    timeZone: "Europe/Madrid",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const year = parts.find((part) => part.type === "year")?.value ?? "";
  const month = parts.find((part) => part.type === "month")?.value ?? "";
  const day = parts.find((part) => part.type === "day")?.value ?? "";
  return `${year}-${month}-${day}`;
}

const COBERTURAS_LLOYDS = [
  "Asistencia Sanitaria: ILIMITADA en centros concertados. (España y Portugal).",
  "Fallecimiento por Accidente: 15.000€ (menores de 14 años, gastos de sepelio 3.005,06€).",
  "Invalidez absoluta y permanente por accidente: 15.000€.",
  "1er. traslado incluido. 2º traslado con prescripción médica.",
  "Asistencia sanitaria en circuitos internacionales por reembolso (máx. de 9.000€).",
  "Competiciones NO Federadas: Incluido.",
];

type PasoInicial = "formulario" | "modalidad" | "tipo";

interface ModalidadDisponible {
  id: number;
  nombre: string;
  tipo: string;
  precio: number;
}

interface ContratarInfo {
  contratable?: boolean;
  motivo?: string;
  circuito: string;
  paso_inicial?: PasoInicial;
  modalidad?: { nombre: string; tipo: string; precio: number; precioBase?: number };
  modalidades_disponibles?: ModalidadDisponible[];
  tipo_precargado?: string;
  requiereDireccion?: boolean;
  aceptaPago?: boolean;
  vendedor?: LegalVendedorInfo;
}

function ResumenSeguroDesplegable({
  open,
  onToggle,
}: {
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50/80">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between px-4 py-3 text-left text-sm font-medium text-[#1A3AB8] hover:bg-gray-100/80"
        aria-expanded={open}
      >
        Resumen del seguro {open ? "-" : "+"}
      </button>
      {open && (
        <div className="border-t border-gray-200 px-4 py-3 text-sm text-[#2C2C2C]">
          <p className="font-medium">Compañía: Lloyd&apos;s</p>
          <p className="mt-2 font-medium">Coberturas:</p>
          <ol className="mt-1 list-decimal space-y-1 pl-5">
            {COBERTURAS_LLOYDS.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

function resumenImportes(precioUnitario: number) {
  const importePrima = precioUnitario;
  const gastosGestion = importePrima * 0.0045;
  const importeTotal = importePrima + gastosGestion;
  return { importePrima, gastosGestion, importeTotal };
}

export function ContratarPage() {
  const { token } = useParams<{ token: string }>();
  const { setLegalVendedor } = useLegalComercio();
  const [loading, setLoading] = useState(true);
  const [invalid, setInvalid] = useState(false);
  const [info, setInfo] = useState<ContratarInfo | null>(null);

  const [pasoUi, setPasoUi] = useState<"tipo" | "modalidad" | "formulario">("formulario");
  const [tipoElegido, setTipoElegido] = useState<"diario" | "anual" | null>(null);
  const [modalidadElegida, setModalidadElegida] = useState<ModalidadDisponible | null>(null);

  const [nombre, setNombre] = useState("");
  const [apellidos, setApellidos] = useState("");
  const [nif, setNif] = useState("");
  const [fechaNacimiento, setFechaNacimiento] = useState("");
  const [email, setEmail] = useState("");
  const [telefono, setTelefono] = useState("");
  const [fecha, setFecha] = useState("");
  const [hora, setHora] = useState("09");
  const [minutos, setMinutos] = useState("00");
  const [direccion, setDireccion] = useState("");
  const [poblacion, setPoblacion] = useState("");
  const [provincia, setProvincia] = useState("");
  const [codigoPostal, setCodigoPostal] = useState("");

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [payLoading, setPayLoading] = useState(false);
  const [registroOk, setRegistroOk] = useState(false);
  const [aceptaCondiciones, setAceptaCondiciones] = useState(false);
  const [resumenOpen, setResumenOpen] = useState(false);

  useEffect(() => {
    setLegalVendedor(info?.vendedor ?? null);
    return () => setLegalVendedor(null);
  }, [info?.vendedor, setLegalVendedor]);

  useEffect(() => {
    if (!token?.trim()) {
      setInvalid(true);
      setLoading(false);
      return;
    }
    const t = token.trim();
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get<ContratarInfo>(`/public/contratar/${t}`);
        if (!cancelled) {
          setInfo(res.data);
          setInvalid(false);
          const pi = res.data.paso_inicial ?? "formulario";
          if (pi === "tipo") {
            setPasoUi("tipo");
            setTipoElegido(null);
            setModalidadElegida(null);
          } else if (pi === "modalidad") {
            setPasoUi("modalidad");
            setTipoElegido(
              res.data.tipo_precargado === "diario" || res.data.tipo_precargado === "anual"
                ? res.data.tipo_precargado
                : null
            );
            setModalidadElegida(null);
          } else {
            setPasoUi("formulario");
            if (res.data.modalidad) {
              setModalidadElegida({
                id: 0,
                nombre: res.data.modalidad.nombre,
                tipo: res.data.modalidad.tipo,
                precio: res.data.modalidad.precio,
              });
            }
          }
        }
      } catch {
        if (!cancelled) {
          setInvalid(true);
          setInfo(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const modalidadActiva = useMemo((): ModalidadDisponible | null => {
    if (info?.paso_inicial === "formulario" && info.modalidad) {
      return {
        id: 0,
        nombre: info.modalidad.nombre,
        tipo: info.modalidad.tipo,
        precio: info.modalidad.precio,
      };
    }
    return modalidadElegida;
  }, [info, modalidadElegida]);

  const modalidadesFiltradas = useMemo(() => {
    const lista = info?.modalidades_disponibles ?? [];
    if (!tipoElegido) return lista;
    return lista.filter((m) => m.tipo === tipoElegido);
  }, [info, tipoElegido]);

  const aceptaPago = info?.aceptaPago === true;

  const importes = useMemo(() => {
    if (!modalidadActiva || !aceptaPago) return null;
    return resumenImportes(Number(modalidadActiva.precio));
  }, [modalidadActiva, aceptaPago]);

  const esDiario = modalidadActiva?.tipo === "diario";
  const esAnual = modalidadActiva?.tipo === "anual";
  const requiereFechaInicio = esDiario || esAnual;
  const requiereDireccion =
    info?.paso_inicial === "formulario"
      ? info?.requiereDireccion === true
      : modalidadActiva?.tipo === "anual";

  const ahora = new Date();
  const horaActual = ahora.getHours();
  const esHoy = fecha === ahora.toISOString().split("T")[0];

  useEffect(() => {
    if (esHoy && parseInt(hora) < horaActual) {
      setHora(String(horaActual).padStart(2, "0"));
      setMinutos("00");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fecha]);

  const fechaInicioLocal = fecha ? `${fecha}T${hora}:${minutos}:00` : "";

  const necesitaModalidadIdEnPago = info?.paso_inicial !== "formulario";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token?.trim() || !info || !modalidadActiva) return;
    setSubmitError(null);

    if (info.contratable === false) {
      setSubmitError(info.motivo ?? "Este enlace ya no permite nuevas contrataciones.");
      return;
    }
    if (requiereFechaInicio && !fechaInicioLocal) {
      setSubmitError("Indica la fecha y hora de inicio de la cobertura.");
      return;
    }
    if (
      requiereFechaInicio &&
      fechaInicioLocal.slice(0, 10) < fechaMadridActual()
    ) {
      setSubmitError("La fecha de inicio debe corresponder a hoy o a una fecha posterior.");
      return;
    }
    if (requiereDireccion) {
      if (!direccion.trim() || !poblacion.trim() || !provincia.trim() || !codigoPostal.trim()) {
        setSubmitError("Completa dirección, población, provincia y código postal.");
        return;
      }
    }
    if (!aceptaCondiciones) {
      setSubmitError("Debes aceptar las condiciones de compra para continuar.");
      return;
    }

    setPayLoading(true);
    try {
      const body: Record<string, string | number | undefined> = {
        nombre: nombre.trim(),
        apellidos: apellidos.trim(),
        nif: nif.trim(),
        fecha_nacimiento: fechaNacimiento.trim(),
        email: email.trim(),
        telefono: telefono.trim(),
        direccion: requiereDireccion ? direccion.trim() : undefined,
        poblacion: requiereDireccion ? poblacion.trim() : undefined,
        provincia: requiereDireccion ? provincia.trim() : undefined,
        codigo_postal: requiereDireccion ? codigoPostal.trim() : undefined,
      };
      if (requiereFechaInicio) {
        body.fecha_inicio = new Date(fechaInicioLocal).toISOString();
      }
      if (necesitaModalidadIdEnPago && modalidadElegida) {
        body.modalidad_id = modalidadElegida.id;
      }

      type ContratarResponse =
        | {
            tipo: "pago";
            seguro_id: number;
            piloto_id?: number;
            formData: {
              Ds_SignatureVersion: string;
              Ds_MerchantParameters: string;
              Ds_Signature: string;
              actionUrl: string;
            };
          }
        | { tipo: "registro"; message: string };

      const res = await api.post<ContratarResponse>(`/public/contratar/${token.trim()}/iniciar-pago`, body);

      sessionStorage.setItem("contratar_gracias_email", email.trim());

      if (res.data.tipo === "registro") {
        setRegistroOk(true);
        setPayLoading(false);
        return;
      }

      if (res.data.tipo === "pago" && res.data.seguro_id) {
        saveContratarPagoCtx(token.trim(), res.data.seguro_id, res.data.piloto_id);
      }
      const { formData } = res.data;
      if (res.data.tipo === "pago" && formData?.actionUrl) {
        submitRedsysForm(formData);
      }
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number; data?: { error?: string } } })?.response
        ?.status;
      const backendMsg = (err as { response?: { status?: number; data?: { error?: string } } })
        ?.response?.data?.error;

      if (status === 409) {
        setSubmitError(
          backendMsg ?? "Ya existe un seguro con estos datos para esta fecha."
        );
      } else if (status === 400) {
        setSubmitError(backendMsg ?? "Revisa los datos del formulario e inténtalo de nuevo.");
      } else {
        setSubmitError(
          "No hemos podido completar tu registro. Inténtalo de nuevo o contacta con el circuito."
        );
      }
      setPayLoading(false);
    }
  };

  if (loading) {
    return (
      <PublicPageFrame>
        <div className={`${cardClass} max-w-md text-center`}>
          <Logo className="mx-auto mb-6 h-10 scale-[2] object-contain" />
          <p className="text-[#2C2C2C]">Cargando…</p>
        </div>
      </PublicPageFrame>
    );
  }

  if (invalid || !info) {
    return (
      <PublicPageFrame>
        <div className={`${cardClass} max-w-md text-center`}>
          <Logo className="mx-auto mb-6 h-10 scale-[2] object-contain" />
          <p className="text-lg font-medium text-[#2C2C2C]">Enlace no válido o desactivado</p>
          <p className="mt-2 text-sm text-gray-600">
            Si necesitas un seguro, pide al circuito un código QR activo.
          </p>
        </div>
      </PublicPageFrame>
    );
  }

  if (info.contratable === false) {
    return (
      <PublicPageFrame>
        <div className={`${cardClass} max-w-md text-center`}>
          <Logo className="mx-auto mb-6 h-10 scale-[2] object-contain" />
          <p className="text-lg font-medium text-[#2C2C2C]">Contratación no disponible</p>
          <p className="mt-2 text-sm text-gray-600">{info.motivo ?? "Este enlace ya no permite nuevas contrataciones."}</p>
          <p className="mt-4 text-sm text-gray-500">Circuito: {info.circuito}</p>
        </div>
      </PublicPageFrame>
    );
  }

  if (registroOk) {
    return (
      <PublicPageFrame>
        <div className={`${cardClass} max-w-xl text-center`}>
          <Logo className="mx-auto mb-6 h-10 scale-[2] object-contain" />
          <p className="text-lg font-semibold text-green-700">Registro completado</p>
          <p className="mt-3 text-sm text-gray-700">
            El circuito validará tu acceso antes de entrar a pista.
          </p>
        </div>
      </PublicPageFrame>
    );
  }

  if (pasoUi === "tipo") {
    return (
      <PublicPageFrame>
        <div className={`${cardClass} max-w-xl`}>
          <div className="mb-6 flex justify-center">
            <Logo className="h-10 scale-[2] object-contain" />
          </div>
          <h1 className="text-xl font-semibold text-[#2C2C2C]">{TITULO_SEGURO}</h1>
          <p className="mt-2 text-sm text-gray-600">Elige el tipo de cobertura</p>
          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            <button
              type="button"
              className="rounded-xl border-2 border-gray-200 p-6 text-left transition-colors hover:border-[#1A3AB8] hover:bg-blue-50/40"
              onClick={() => {
                setTipoElegido("diario");
                setPasoUi("modalidad");
              }}
            >
              <span className="text-lg font-semibold text-[#1A3AB8]">Diario</span>
              <p className="mt-1 text-sm text-gray-600">Cobertura por día o sesión</p>
            </button>
            <button
              type="button"
              className="rounded-xl border-2 border-gray-200 p-6 text-left transition-colors hover:border-[#1A3AB8] hover:bg-blue-50/40"
              onClick={() => {
                setTipoElegido("anual");
                setPasoUi("modalidad");
              }}
            >
              <span className="text-lg font-semibold text-[#1A3AB8]">Anual</span>
              <p className="mt-1 text-sm text-gray-600">Cobertura anual con domicilio</p>
            </button>
          </div>
        </div>
      </PublicPageFrame>
    );
  }

  if (pasoUi === "modalidad") {
    return (
      <PublicPageFrame>
        <div className={`${cardClass} max-w-xl`}>
          <div className="mb-6 flex justify-center">
            <Logo className="h-10 scale-[2] object-contain" />
          </div>
          <h1 className="text-xl font-semibold text-[#2C2C2C]">{TITULO_SEGURO}</h1>
          <p className="mt-2 text-sm text-gray-600">
            {tipoElegido === "anual" ? "Modalidades anuales" : tipoElegido === "diario" ? "Modalidades diarias" : "Elige tu modalidad"}
          </p>
          {info.paso_inicial === "tipo" && (
            <button
              type="button"
              className="mt-2 text-sm text-[#1A3AB8] hover:underline"
              onClick={() => {
                setPasoUi("tipo");
                setModalidadElegida(null);
              }}
            >
              ← Cambiar tipo
            </button>
          )}
          <ul className="mt-6 space-y-3">
            {modalidadesFiltradas.map((m) => (
              <li key={m.id}>
                <button
                  type="button"
                  className="w-full rounded-xl border-2 border-gray-200 p-4 text-left transition-colors hover:border-[#1A3AB8] hover:bg-blue-50/40"
                  onClick={() => {
                    setModalidadElegida(m);
                    setPasoUi("formulario");
                  }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-[#2C2C2C]">{m.nombre}</span>
                    <span className="text-lg font-bold text-[#1A3AB8]">{Number(m.precio).toFixed(2)} €</span>
                  </div>
                  <span className="text-xs text-gray-500 capitalize">{m.tipo}</span>
                </button>
              </li>
            ))}
          </ul>
          {modalidadesFiltradas.length === 0 && (
            <p className="mt-4 text-sm text-red-600">No hay modalidades disponibles para esta selección.</p>
          )}
        </div>
      </PublicPageFrame>
    );
  }

  if (!modalidadActiva) {
    return (
      <PublicPageFrame>
        <div className={`${cardClass} max-w-md text-center`}>
          <Logo className="mx-auto mb-6 h-10 scale-[2] object-contain" />
          <p className="text-lg font-medium text-[#2C2C2C]">Selecciona una modalidad</p>
          <Button className="mt-4" onClick={() => setPasoUi(info.paso_inicial === "tipo" ? "tipo" : "modalidad")}>
            Volver
          </Button>
        </div>
      </PublicPageFrame>
    );
  }

  return (
    <PublicPageFrame>
      <div className={`${cardClass} max-w-xl`}>
        <div className="mb-6 flex justify-center">
          <Logo className="h-10 scale-[2] object-contain" />
        </div>
        <h1 className="text-xl font-semibold text-[#2C2C2C]">{TITULO_SEGURO}</h1>
        <p className="mt-1 text-sm text-gray-600">{modalidadActiva.nombre}</p>
        <p className="text-2xl font-bold text-[#1A3AB8] mt-1">
          {Number(modalidadActiva.precio).toFixed(2)} €
          <span className="text-sm font-normal text-gray-500 ml-1">por piloto</span>
        </p>
        {info.paso_inicial !== "formulario" && (
          <button
            type="button"
            className="mt-2 text-sm text-[#1A3AB8] hover:underline"
            onClick={() => {
              setModalidadElegida(null);
              setPasoUi("modalidad");
            }}
          >
            ← Cambiar modalidad
          </button>
        )}
        {!aceptaPago && (
          <p className="text-sm text-gray-500 mt-1">El circuito/organizador gestionará el cobro directamente</p>
        )}

        <ResumenSeguroDesplegable open={resumenOpen} onToggle={() => setResumenOpen((v) => !v)} />

        <form onSubmit={(e) => void handleSubmit(e)} className="mt-6 space-y-4">
          <Input label="Nombre" value={nombre} onChange={(e) => setNombre(e.target.value)} required />
          <Input label="Apellidos" value={apellidos} onChange={(e) => setApellidos(e.target.value)} required />
          <Input label="NIF" value={nif} onChange={(e) => setNif(e.target.value)} required />
          <Input
            label="Fecha de nacimiento (DD/MM/AAAA)"
            value={fechaNacimiento}
            onChange={(e) => setFechaNacimiento(e.target.value)}
            required
          />
          <Input
            type="email"
            label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <Input label="Teléfono" value={telefono} onChange={(e) => setTelefono(e.target.value)} required />

          {requiereFechaInicio && (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <div className="col-span-2 sm:col-span-1">
                <Input
                  label="Fecha de inicio de la cobertura"
                  type="date"
                  min={new Date().toISOString().split("T")[0]}
                  value={fecha}
                  onChange={(e) => setFecha(e.target.value)}
                  required
                />
              </div>
              <div>
                <Select
                  label="Hora"
                  value={hora}
                  onChange={(e) => setHora(e.target.value)}
                  options={Array.from({ length: 24 }, (_, i) => ({
                    value: String(i).padStart(2, "0"),
                    label: `${String(i).padStart(2, "0")}:00`,
                  })).filter((opt) => !esHoy || parseInt(opt.value) >= horaActual)}
                />
              </div>
              <div>
                <Select
                  label="Minutos"
                  value={minutos}
                  onChange={(e) => setMinutos(e.target.value)}
                  options={[
                    { value: "00", label: "00" },
                    { value: "15", label: "15" },
                    { value: "30", label: "30" },
                    { value: "45", label: "45" },
                  ]}
                />
              </div>
            </div>
          )}

          {requiereDireccion && (
            <>
              <Input label="Dirección" value={direccion} onChange={(e) => setDireccion(e.target.value)} required />
              <Input label="Población" value={poblacion} onChange={(e) => setPoblacion(e.target.value)} required />
              <Select
                label="Provincia"
                value={provincia}
                onChange={(e) => setProvincia(e.target.value)}
                options={[{ value: "", label: "Selecciona provincia" }, ...PROVINCIAS_ESPANA_OPTIONS]}
                required
              />
              <Input
                label="Código postal"
                value={codigoPostal}
                onChange={(e) => setCodigoPostal(e.target.value)}
                required
              />
            </>
          )}

          {aceptaPago && importes && (
            <>
              <p className="text-sm text-gray-700">
                Se realizará un pago de <strong>{importes.importeTotal.toFixed(2)} €</strong> con tarjeta (incluye
                gastos de gestión).
              </p>
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-[#2C2C2C]">
                <p className="flex justify-between">
                  <span>Prima (1 piloto)</span>
                  <span>{importes.importePrima.toFixed(2)} €</span>
                </p>
                <p className="mt-1 flex justify-between text-gray-600">
                  <span>Gastos de gestión</span>
                  <span>{importes.gastosGestion.toFixed(2)} €</span>
                </p>
                <p className="mt-2 flex justify-between border-t border-gray-200 pt-2 font-semibold">
                  <span>Total a pagar</span>
                  <span>{importes.importeTotal.toFixed(2)} €</span>
                </p>
              </div>
            </>
          )}

          {!aceptaPago && (
            <p className="text-sm text-gray-600">Completa el formulario para registrarte. El circuito/organizador gestionará el cobro directamente.</p>
          )}

          {submitError && <p className="text-sm text-red-600">{submitError}</p>}

          <AceptacionCondicionesPago
            checked={aceptaCondiciones}
            onChange={setAceptaCondiciones}
            id="acepta-condiciones-contratar"
          />

          <Button
            type="submit"
            className="w-full"
            loading={payLoading}
            disabled={payLoading || !aceptaCondiciones}
          >
            {aceptaPago ? "Asegurarme y pagar" : "Asegurarme"}
            {aceptaPago && importes ? ` — ${importes.importeTotal.toFixed(2)} €` : ""}
          </Button>
          {payLoading && aceptaPago && (
            <p className="text-center text-sm text-gray-500">Redirigiendo al banco…</p>
          )}
        </form>
      </div>
    </PublicPageFrame>
  );
}
